import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { InputText } from 'primereact/inputtext';
import { InputTextarea } from 'primereact/inputtextarea';
import { Dropdown } from 'primereact/dropdown';
import { InputNumber } from 'primereact/inputnumber';
import { Toast } from 'primereact/toast';
import { TabView, TabPanel } from 'primereact/tabview';
import Loader from '../../common/components/loader/Loader';
import BatchProcessing from './components/BatchProcessing/BatchProcessing';
import RulesManagement from './components/RulesManagement/RulesManagement';
import * as actionTypes from './redux/actionTypes';
import './DataCategorization.scss';

// API URL constant
const API_BASE_URL = '/api/v1/data-categorization/categories';

const DataCategorization = () => {
  const dispatch = useDispatch();
  const { loading, error, successMessage } = useSelector(
    (state) => state.dataCategorization
  );

  const [displayDialog, setDisplayDialog] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [activeTabIndex, setActiveTabIndex] = useState(0);

  const handleTabChange = (e) => {
    setActiveTabIndex(e.index);
  };
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    categoryType: '',
    status: 'ACTIVE',
    priority: 0
  });
  const toast = React.useRef(null);

  const statusOptions = [
    { label: 'Active', value: 'ACTIVE' },
    { label: 'Inactive', value: 'INACTIVE' }
  ];

  useEffect(() => {
    dispatch({ type: actionTypes.GET_ALL_DATA_CATEGORIES });
  }, [dispatch]);

  useEffect(() => {
    if (error) {
      toast.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: error,
        life: 3000,
      });
      dispatch({ type: actionTypes.CLEAR_DATA_CATEGORY_ERROR });
    }
  }, [error, dispatch]);

  useEffect(() => {
    if (successMessage) {
      toast.current?.show({
        severity: 'success',
        summary: 'Success',
        detail: successMessage,
        life: 3000,
      });
      // Auto-clear success message after showing
      const timer = setTimeout(() => {
        dispatch({ type: actionTypes.CLEAR_SUCCESS_MESSAGE });
        setDisplayDialog(false);
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [successMessage, dispatch]);

  const handleSaveCategory = () => {
    // Validate required fields
    if (!formData.name.trim()) {
      toast.current?.show({
        severity: 'warn',
        summary: 'Validation Error',
        detail: 'Category name is required',
        life: 3000,
      });
      return;
    }

    if (formData.name.length > 255) {
      toast.current?.show({
        severity: 'warn',
        summary: 'Validation Error',
        detail: 'Category name must not exceed 255 characters',
        life: 3000,
      });
      return;
    }

    if (formData.description && formData.description.length > 1000) {
      toast.current?.show({
        severity: 'warn',
        summary: 'Validation Error',
        detail: 'Description must not exceed 1000 characters',
        life: 3000,
      });
      return;
    }

    // Prepare data for API
    const categoryData = {
      name: formData.name.trim(),
      description: formData.description.trim() || null,
      categoryType: formData.categoryType.trim() || null,
      priority: formData.priority || 0
    };

    if (isEditMode) {
      // Update existing category
      dispatch({
        type: actionTypes.UPDATE_DATA_CATEGORY,
        payload: {
          id: formData.id,
          data: categoryData,
          userId: 'SYSTEM'
        }
      });
    } else {
      // Create new category
      dispatch({
        type: actionTypes.CREATE_DATA_CATEGORY,
        payload: {
          data: categoryData,
          userId: 'SYSTEM'
        }
      });
    }
  };

  const handleFormInputChange = (field, value) => {
    setFormData({
      ...formData,
      [field]: value
    });
  };

  if (loading) {
    return <Loader />;
  }

  return (
    <div className="data-categorization-container">
      <Toast ref={toast} />

      <div className="header-section">
        <div>
          <h1>Data Categories</h1>
          <p className="subtitle">Manage data categories for automatic categorization</p>
        </div>
      </div>

      <TabView activeIndex={activeTabIndex} onTabChange={handleTabChange}>
        <TabPanel header="Categorization Rules">
          <RulesManagement />
        </TabPanel>
        <TabPanel header="Excel Upload">
          <BatchProcessing />
        </TabPanel>
        {/* <TabPanel header="Categories">
          <div style={{ marginBottom: '1rem' }}>
            <Button
              label="Add New Category"
              icon="pi pi-plus"
              onClick={handleOpenDialog}
              className="p-button-success"
            />
          </div>
          <DataTable
            value={categories}
            className="p-datatable-striped"
            paginator
            rows={10}
            rowsPerPageOptions={[5, 10, 20]}
            responsive
            emptyMessage="No categories found. Create your first category to get started."
          >
            <Column field="id" header="ID" sortable style={{ width: '80px' }} />
            <Column field="name" header="Name" sortable />
            <Column field="description" header="Description" />
            <Column field="categoryType" header="Type" sortable />
            <Column field="status" header="Status" sortable />
            <Column field="priority" header="Priority" sortable style={{ width: '100px' }} />
            <Column body={actionBodyTemplate} header="Actions" style={{ width: '150px' }} />
          </DataTable>
        </TabPanel> */}
      </TabView>

      <Dialog
        visible={displayDialog}
        style={{ width: '600px', maxHeight: '90vh' }}
        header={isEditMode ? 'Edit Category' : 'Add Category'}
        modal
        onHide={() => setDisplayDialog(false)}
      >
        <div className="p-fluid">
          <div className="form-group" style={{ marginBottom: '1.5rem' }}>
            <label htmlFor="categoryName">Category Name *</label>
            <InputText
              id="categoryName"
              value={formData.name}
              onChange={(e) => handleFormInputChange('name', e.target.value)}
              placeholder="e.g., Finance, Technical, Customer"
              maxLength="255"
            />
            <small style={{ color: '#999' }}>{formData.name.length}/255</small>
          </div>

          <div className="form-group" style={{ marginBottom: '1.5rem' }}>
            <label htmlFor="categoryDescription">Description</label>
            <InputTextarea
              id="categoryDescription"
              value={formData.description}
              onChange={(e) => handleFormInputChange('description', e.target.value)}
              placeholder="Optional description for this category"
              maxLength="1000"
              rows={3}
            />
            <small style={{ color: '#999' }}>{formData.description.length}/1000</small>
          </div>

          <div className="form-group" style={{ marginBottom: '1.5rem' }}>
            <label htmlFor="categoryType">Category Type</label>
            <InputText
              id="categoryType"
              value={formData.categoryType}
              onChange={(e) => handleFormInputChange('categoryType', e.target.value)}
              placeholder="e.g., FINANCIAL, TECHNICAL"
              maxLength="100"
            />
          </div>

          <div className="form-group" style={{ marginBottom: '1.5rem' }}>
            <label htmlFor="categoryStatus">Status</label>
            <Dropdown
              id="categoryStatus"
              value={formData.status}
              options={statusOptions}
              onChange={(e) => handleFormInputChange('status', e.value)}
            />
          </div>

          <div className="form-group" style={{ marginBottom: '1.5rem' }}>
            <label htmlFor="categoryPriority">Priority</label>
            <InputNumber
              id="categoryPriority"
              value={formData.priority}
              onValueChange={(e) => handleFormInputChange('priority', e.value)}
              min={0}
              max={1000}
            />
          </div>
        </div>

        <div className="dialog-buttons" style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem', marginTop: '1rem' }}>
          <Button
            label="Cancel"
            icon="pi pi-times"
            onClick={() => setDisplayDialog(false)}
            className="p-button-secondary"
          />
          <Button
            label={isEditMode ? 'Update' : 'Create'}
            icon="pi pi-check"
            onClick={handleSaveCategory}
            loading={loading}
            className="p-button-success"
          />
        </div>
      </Dialog>
    </div>
  );
};

export default DataCategorization;
